import { useState, useEffect } from 'react';
import type { MouseEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'ホーム', path: '/' },
  { label: 'サービス', path: '/services' },
  { label: '料金プラン', path: '/plans' },
  { label: 'ニュース', path: '/news' },
  { label: '会社概要', path: '/about' },
  { label: 'お問い合わせ', path: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const handleTopNavigation = (event: MouseEvent<HTMLAnchorElement>, path: string) => {
    const [pathname, hash = ''] = path.split('#');
    const targetPath = pathname || location.pathname;

    if (targetPath !== location.pathname) {
      return;
    }

    event.preventDefault();

    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      return;
    }

    const sectionId = ['system', 'app', 'web'].includes(hash) ? 'services-tabs' : hash;
    const section = document.getElementById(sectionId);

    if (!section) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      return;
    }

    window.location.hash = hash;
    section.scrollIntoView({ behavior: 'auto', block: 'start' });
    window.scrollBy(0, -96);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" onClick={(event) => handleTopNavigation(event, '/')} className="flex items-center gap-2 cursor-pointer">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-teal-400 to-cyan-500">
            <i className="ri-water-flash-line text-white text-sm"></i>
          </div>
          <span
            className={`font-bold text-lg tracking-wide transition-colors duration-300 ${
              scrolled ? 'text-teal-700' : 'text-white'
            }`}
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Ocean
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={(event) => handleTopNavigation(event, link.path)}
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                location.pathname === link.path
                  ? scrolled
                    ? 'text-teal-600 border-b-2 border-teal-500 pb-0.5'
                    : 'text-teal-200 border-b-2 border-teal-200 pb-0.5'
                  : scrolled
                  ? 'text-gray-600 hover:text-teal-600'
                  : 'text-white/90 hover:text-white'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contact"
            onClick={(event) => handleTopNavigation(event, '/contact')}
            className="ml-2 px-4 py-2 rounded-full bg-gradient-to-r from-teal-400 to-cyan-500 text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
          >
            無料相談
          </Link>
        </nav>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden w-8 h-8 flex items-center justify-center cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="メニュー"
        >
          <i
            className={`text-xl transition-colors duration-300 ${
              scrolled ? 'text-gray-700' : 'text-white'
            } ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}
          ></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white/98 backdrop-blur-md border-t border-gray-100 px-4 py-4 flex flex-col gap-3">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={(event) => handleTopNavigation(event, link.path)}
              className={`text-sm font-medium py-2 px-3 rounded-lg cursor-pointer transition-colors ${
                location.pathname === link.path
                  ? 'bg-teal-50 text-teal-600'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contact"
            onClick={(event) => handleTopNavigation(event, '/contact')}
            className="mt-1 px-4 py-2 rounded-full bg-gradient-to-r from-teal-400 to-cyan-500 text-white text-sm font-semibold text-center whitespace-nowrap cursor-pointer"
          >
            無料相談
          </Link>
        </div>
      )}
    </header>
  );
}
